'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { LuMail } from 'react-icons/lu';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section
      id="newsletters"
      className={cn(' w-full bg-black px-3 py-4 md:mb-5 md:p-12', 'ea-homepage-newsletter')}
    >
      <Container className="flex items-center justify-center p-0">
        <form
          className={cn(
            'flex w-full max-w-3xl flex-col gap-6 py-2',
            'ea-homepage-newsletter__form',
          )}
          onSubmit={handleSubmit}
        >
<CaptchaRequestMessage error={subscribeMutation.error} />
          <div className={cn('flex flex-col gap-2', 'ea-homepage-newsletter__form-title')}>
            <h2
              data-ea-visual-tag="h4"
              className={cn(
                'max-w-sm text-3xl font-bold uppercase text-white md:text-[40px]',
                'ea-homepage-newsletter__form-title-main',
              )}
            >
              {t('text610')}
            </h2>
          </div>
          <div
            className={cn(
              'col-span-12 flex flex-col justify-center space-y-3 md:col-span-5',
              'ea-homepage-newsletter__form-item-email',
            )}
          >
            <div className={cn('flex flex-col items-center gap-3 md:flex-row')}>
              <Input
                aria-label={systemLabels('email')}
                id={`newsletter-email-${seoNewsletterId}`}
                type="email"
                name="email"
                placeholder={t('text183')}
                onChange={(e) => setEmail(e.target.value)}
                className={{
                  wrapper: cn(
                    'rounded-none border-none bg-input pr-1 text-muted-foreground',
                    'ea-homepage-newsletter__form-item-email-input',
                  ),
                }}
              />
              <Button
                type="submit"
                className={cn(
                  'w-full rounded-none border-none bg-primary px-6 py-3 text-primary-foreground md:max-w-fit',
                  'ea-homepage-newsletter__form-item-email-submit-button',
                )}
                disabled={email.length === 0 || !checked || isSubscribed}
                isLoading={subscribeMutation.isPending}
              >
                {t('text182')}
              </Button>
            </div>
            {isSubscribed && <span className={cn('text-success')}>{t('text234')}</span>}
            <div
              className={cn(
                'flex flex-row items-center space-x-2 py-2',
                'ea-homepage-newsletter__form-item-gdpr',
              )}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => setChecked(!checked)}
                id={`newsletter-gdpr-${seoNewsletterId}`}
                aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
                className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
                required
              />
              <Label
                htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
                className={cn(
                  ' text-white',
                  'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
                )}
              >
                <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                  {t('text264')}&nbsp;
                  <a
                    href={t('text391')}
                    className={cn(
                      'text-primary',
                      'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                    )}
                  >
                    {t('text265')}
                  </a>{' '}
                  <a
                    href={t('text392')}
                    className={cn(
                      'text-primary',
                      'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                    )}
                  >
                    {t('text436')}
                  </a>
                  .
                </span>
              </Label>
            </div>
          </div>
        </form>
      </Container>
    </section>
  );
}
